import React from "react";
import Image from "next/image";
import { cn } from "@/shared/utils";
import Forbes from "@/shared/components/PolicyCalloutTest/assets/forbes.webp";
import MarketWatch from "@/shared/components/PolicyCalloutTest/assets/marketwatch.webp";
import TrustPilot from "@/shared/components/PolicyCalloutTest/assets/trustpilot.webp";
import TrustPilotRebrand from "@/shared/media/images/trustpilot-rebrand.webp";
import MarketWatchRebrand from "@/shared/media/images/market-watch-rebrand.webp";
import ForbesRebrand from "@/shared/media/images/forbes-rebrand.webp";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";
import ForbesSVG from "@/shared/components/PolicyCalloutTest/assets/forbes.svg";
import MarketWatchSVG from "@/shared/components/PolicyCalloutTest/assets/marketwatch.svg";
import TrustPilotSVG from "@/shared/components/PolicyCalloutTest/assets/trustpilot.svg";
import Accolade from "./Accolade";

interface AccoladesProps {
    className?: string;
    variant?: "default" | "whySpot" | "coverage";
}

export const Accolades: React.FC<AccoladesProps> = ({ className, variant = "default" }) => {
    const isWhySpot = variant === "whySpot";

    const IMAGE_SIZES = isWhySpot ? "w-full sm:w-[192px] md:w-[249px] lg:w-[203px] xl:w-[285px]" : "w-full max-w-[160px] xs:max-w-[190px]";
    const { showRebrandTheme } = useThemeContext().theme;
    const trustPilotImage = showRebrandTheme ? TrustPilotRebrand : TrustPilot;
    const marketWatchImage = showRebrandTheme ? MarketWatchRebrand : MarketWatch;
    const forbesImage = showRebrandTheme ? ForbesRebrand : Forbes;
    const isCoverage = variant === "coverage";

    // Coverage accolades use the Accolade component to show/hide laurels and stars
    if (isCoverage) {
        const logoClass = "text-content-brand-b h-6 lg:h-8";
        return (
            <div className="flex items-center justify-between">
                <Accolade
                    logoImg={<MarketWatchSVG className={logoClass} />}
                    text={
                        <>
                            &quot;Most
                            <br />
                            Customizable&quot;
                        </>
                    }
                    logoTextGap="gap-1"
                />
                <Accolade
                    logoImg={<ForbesSVG className={logoClass} />}
                    text={
                        <>
                            &quot;Best for Breadth
                            <br />
                            of Coverage&quot;
                        </>
                    }
                    logoTextGap="gap-2"
                />
                <Accolade logoImg={<TrustPilotSVG className={logoClass} />} text="4.7 stars" showStars starsPosition="below" logoTextGap="gap-1" />
            </div>
        );
    }

    return (
        <div
            className={cn(
                "grid w-fit grid-cols-2 items-center gap-3",
                isWhySpot ? "lg:flex lg:flex-wrap lg:justify-center xl:grid xl:w-full xl:grid-cols-3" : "xs:gap-4 lg:flex lg:flex-wrap",
                className
            )}
        >
            <Image priority src={trustPilotImage} alt="Trustpilot" className={IMAGE_SIZES} />
            <Image priority src={marketWatchImage} alt="Market Watch" className={IMAGE_SIZES} />
            <Image priority src={forbesImage} alt="Forbes" className={cn(IMAGE_SIZES, isWhySpot && "xl:order-first")} />
        </div>
    );
};
